import { Button, InlineMessage } from '@fountain/fountain-ui-components';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import { humanizeKey } from 'containers/OfferLetterTemplates/constants';
import sendOfferMessages from 'containers/SendOffer/messages';
import { fieldProblems, signerBlocks } from 'containers/SendOffer/readiness';
import {
  SendOfferApprovalNotice,
  SendOfferForm,
} from 'containers/SendOffer/SendOfferForm';
import { ApprovalState } from 'containers/SendOffer/types';
import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';
import { ApprovalPlan, PlanOutcome } from './types';
import { OfferEdit } from './useOfferEdit';

interface OfferEditFormProps {
  edit: OfferEdit;
  /** Who the offer is for, for the form's opening sentence. */
  applicantName: string | null;
  /** The offer's own status, which decides what editing it does. */
  offerStatus: string | null;
}

/**
 * What editing does to the document, keyed on where the offer stands. It never
 * names an approval: which approvals an edit needs depends on what changes, and
 * the notice below the form answers that as the values change.
 *
 * An offer still waiting on a decision, or whose send failed, starts approval
 * over: the edit cancels the open request. An approved offer that has not gone
 * out keeps its approvals, and the edit holds its send. A rejected offer has
 * nothing to lose, and being asked again is the point of revising it.
 *
 * The Send Offer drawer shows the approved-offer notice over its own edit of a
 * held offer.
 */
export const EditNotice: React.FC<{ offerStatus: string | null }> = ({
  offerStatus,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  if (offerStatus === 'pending_approval' || offerStatus === 'send_failed') {
    return (
      <InlineMessage
        className={styles.editWarning}
        message={intl.formatMessage(messages.editRestartsApproval)}
        type="orange"
      />
    );
  }
  if (offerStatus === 'approved' || offerStatus === 'ready_to_send') {
    return (
      <InlineMessage
        className={styles.editWarning}
        component="div"
        message={
          <>
            <FormattedMessage {...messages.editApprovedTitle} />
            <Typography
              className={styles.editWarningBody}
              component="span"
              variant="body2"
            >
              <FormattedMessage {...messages.editApprovedBody} />
            </Typography>
          </>
        }
        type="orange"
      />
    );
  }
  return null;
};

/**
 * The offer form, inside the review panel (AXHE-4759).
 *
 * `SendOfferForm` unchanged — the same fields, in the same order, with the same
 * conditional-section and formula behavior the recruiter who raised the offer
 * saw. A panel-local form would be a second rendering of one template, and the
 * two would disagree about which fields a template asks for on the first change
 * to either.
 *
 * `onBackToDocuments` is deliberately not passed: the panel is scoped to ONE
 * offer, the one under review. The document list belongs to a flow that is
 * choosing which document to send, which this is not.
 */
export const OfferEditForm: React.FC<OfferEditFormProps> = ({
  edit,
  applicantName,
  offerStatus,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  if (edit.isError) {
    return (
      <Box px={3} py={4} role="alert">
        <Typography color="error">
          <FormattedMessage {...messages.formLoadError} />
        </Typography>
      </Box>
    );
  }

  // Ordered after the error and keyed on the DATA, not on `isLoading`: a form
  // re-entered after a Cancel refetches, and holding the definition it already
  // has keeps the fields on screen through that instead of flashing a spinner
  // over them.
  if (!edit.form) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress
          size={24}
          aria-label={intl.formatMessage(messages.loadingForm)}
        />
      </Box>
    );
  }

  return (
    <div className={styles.editBody}>
      <EditNotice offerStatus={offerStatus} />

      {/* Named, because an approver reviewing several offers in a sitting has
          no other cue on this view about which one they are editing — the
          letter is gone and the rail with it. Absent rather than printing an
          empty name when the payload carries none. */}
      {applicantName && (
        <Typography variant="body1">
          <FormattedMessage
            {...messages.editIntro}
            values={{ applicant: applicantName }}
          />
        </Typography>
      )}

      <SendOfferForm
        conditionValues={edit.conditionValues}
        form={edit.form}
        locked={edit.locked}
        onConditionChange={edit.setCondition}
        onFieldChange={edit.setField}
        preview={edit.readiness}
        values={edit.values}
      />
    </div>
  );
};

const NO_APPROVAL: ApprovalState = { required: false, triggered_rules: [] };

/**
 * The approval an unsaved edit needs, as the notice states it: every rule with
 * a level the edit asks again, and whether any approval stands beside them.
 * Nothing when the edit asks nobody, or while the answer is not in. The Send
 * Offer drawer states its held-offer edits with it too.
 */
export const editApproval = (plan: ApprovalPlan | null) => {
  if (plan?.outcome !== 'approval')
    return { approval: NO_APPROVAL, keepsApprovals: false };

  const levels = plan.rules.flatMap(rule =>
    rule.levels.map(level => ({ rule, level })),
  );
  const asked = plan.rules.filter(rule =>
    rule.levels.some(
      level => level.status === 'pending' || level.status === 'active',
    ),
  );
  return {
    approval: {
      required: asked.length > 0,
      triggered_rules: asked.map(rule => ({
        label: rule.name ?? '',
        description: '',
        field_key: null,
      })),
    },
    keepsApprovals: levels.some(({ level }) => level.carried),
  };
};

/**
 * "Approval required for this document", for the edit on the form as it
 * stands (the prototype's "Approval required" state on the details step).
 *
 * The drawer's own notice answers what a FIRST send of these values would need,
 * which is the wrong question for an edit: a start date nobody's rule reads
 * needs nothing, and a stage that exempts edits needs nothing at all. So the
 * panel hands it the server's answer for this edit (`draftPlan`), and names
 * every approval that still stands when some do.
 *
 * Rendered by the panel between the form and its footer, where the drawer
 * renders its own, so it stays on screen beside the button it is about.
 *
 * While a change is asked about it keeps the last answer's box, hidden from
 * sight and from assistive tech, so it still says nothing for values the person
 * has replaced. Dropping the box would grow the scroll region above it, and a
 * reader at the form's end would watch the field they are typing in jump. An
 * answer that adds or drops the notice still resizes the region once, when it
 * lands.
 */
export const EditApprovalNotice: React.FC<{ edit: OfferEdit }> = ({ edit }) => {
  const styles = useStyles();
  const [shown, setShown] = useState(edit.draftPlan);
  useEffect(() => {
    if (!edit.draftPlanPending) setShown(edit.draftPlan);
  }, [edit.draftPlan, edit.draftPlanPending]);

  if (!edit.form) return null;

  const { approval, keepsApprovals } = editApproval(
    edit.draftPlanPending ? shown : edit.draftPlan,
  );
  return (
    <SendOfferApprovalNotice
      approval={approval}
      className={classNames(styles.editApprovalNotice, {
        [styles.editApprovalNoticePending]: edit.draftPlanPending,
      })}
      form={edit.form}
      note={
        keepsApprovals ? (
          <FormattedMessage {...messages.editKeepsOtherApprovals} />
        ) : undefined
      }
    />
  );
};

interface OfferEditFooterProps {
  edit: OfferEdit;
  onCancel: () => void;
}

/**
 * The form's own footer: back out, or save and look at what it produced.
 *
 * A sibling of the panel's content rather than the end of the form, so the one
 * control that advances is reachable without scrolling past every field of a
 * long template — the same reason `SendOfferFormFooter` is mounted that way.
 * Written here rather than reused because the primary label differs: this flow
 * is producing a revision of a document already under approval, and "Preview
 * document" would not say that.
 */
export const OfferEditFooter: React.FC<OfferEditFooterProps> = ({
  edit,
  onCancel,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const { readiness } = edit;
  // The two kinds of block a failed check reports, which say different things: a
  // signer block names a role and a remedy that is not on this form, and only
  // the field ones are counted as fields.
  const signers = signerBlocks(readiness);
  const fields = fieldProblems(readiness);
  const blocked = Boolean(readiness && !readiness.can_generate);

  return (
    <div className={styles.footer}>
      {/* Announced, not merely rendered: a failed check is the entire result of
          pressing the CTA, and it appears at the far end of the panel from
          wherever the caret is. The region is mounted with the footer and stays
          empty until there is something to say — a live region inserted at the
          same moment as its content is announced inconsistently. */}
      <div role="status">
        {blocked && (
          <>
            {signers.map(block => (
              <Typography
                className={styles.editFooterNote}
                key={block.key}
                variant="body2"
              >
                <FormattedMessage
                  {...sendOfferMessages.signerNotOnTemplate}
                  values={{ role: humanizeKey(block.role) }}
                />
              </Typography>
            ))}
            {/* Also carries the degenerate case — blocked with nothing itemised
                — so it is deliberately not `fields.length > 0`. Going silent in
                front of a refused save is worse than a count of zero. */}
            {(fields.length > 0 || signers.length === 0) && (
              <Typography className={styles.editFooterNote} variant="body2">
                <FormattedMessage
                  {...sendOfferMessages.cannotSend}
                  values={{ count: fields.length }}
                />
              </Typography>
            )}
          </>
        )}
      </div>

      {edit.error && (
        <Typography className={styles.footerError} variant="body2" role="alert">
          {edit.error}
        </Typography>
      )}

      <div className={styles.footerActions}>
        <Button autoWidth disabled={edit.locked} onClick={onCancel} type="secondary">
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        <Button
          autoWidth
          // Named explicitly because the label is swapped for a spinner while
          // the save is in flight, which would otherwise leave the control with
          // no accessible name at the moment somebody is waiting on it.
          aria-label={intl.formatMessage(messages.previewNewOffer)}
          disabled={edit.busy}
          onClick={edit.preview}
          type="primary"
        >
          {edit.busy ? (
            <CircularProgress size={18} />
          ) : (
            <FormattedMessage {...messages.previewNewOffer} />
          )}
        </Button>
      </div>
    </div>
  );
};

interface EditedDraftFooterProps {
  edit: OfferEdit;
  /** Back to the form, with the entered values still in it. */
  onEdit: () => void;
  /** Abandon the edit and return to the offer as it now stands. */
  onCancel: () => void;
  /** Send it back through the rules. Wraps `edit.submit` to record the attempt. */
  onSubmit: () => void;
}

/**
 * The label names what pressing it does, from the server's answer for the
 * saved edit. With no answer it names approval, the outcome that sends nothing
 * yet. A hold reads as a send too: the same click sends the offer the stage
 * would otherwise hold for a recruiter (see `useOfferEdit`).
 */
const SUBMIT_LABELS: Record<PlanOutcome, MessageDescriptor> = {
  approval: messages.sendForApproval,
  send: messages.sendToApplicant,
  hold: messages.sendToApplicant,
};

/**
 * The footer over an edited draft nobody has been asked about yet (Design
 * Requirements §6, "Edited-draft preview").
 *
 * Cancel does NOT undo the edit — the save that produced this preview has
 * already happened, and on an offer that was still under approval it has already
 * cancelled the open request, which is what the form's banner warned about. What
 * Cancel means here is "do not send this round yet": it returns to the panel,
 * which re-reads the offer and shows the state it is actually in. Nothing is
 * fabricated either way.
 */
export const EditedDraftFooter: React.FC<EditedDraftFooterProps> = ({
  edit,
  onEdit,
  onCancel,
  onSubmit,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const submitLabel = SUBMIT_LABELS[edit.plan?.outcome ?? 'approval'];

  return (
    <div className={styles.footer}>
      {edit.error && (
        <Typography className={styles.footerError} variant="body2" role="alert">
          {edit.error}
        </Typography>
      )}

      <div className={styles.footerGroups}>
        <div className={styles.footerGroup}>
          <Button
            autoWidth
            disabled={edit.locked}
            onClick={onEdit}
            type="secondary"
          >
            <FormattedMessage {...messages.editOffer} />
          </Button>
        </div>
        <div className={styles.footerGroup}>
          <Button
            autoWidth
            disabled={edit.locked}
            onClick={onCancel}
            type="secondary"
          >
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button
            autoWidth
            // Named explicitly for the same reason the form's CTA is: the label
            // is swapped for a spinner while the submit is in flight.
            aria-label={intl.formatMessage(submitLabel)}
            disabled={edit.locked}
            onClick={onSubmit}
            type="primary"
          >
            {edit.locked ? (
              <CircularProgress size={18} />
            ) : (
              <FormattedMessage {...submitLabel} />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
